import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/EquipmentLog.css';
import API_BASE from '../config';

const API = `${API_BASE}/admin`;

function getRowClass(row) {
  if (row.returned) return 'row-returned';              // grey
  const ITEM_KEYS = ['computer','monitor1','monitor2','token','other'];
  const anyPartial = ITEM_KEYS.some(k => row[k + 'Returned']);
  if (anyPartial) return 'row-partial';                  // orange – partial return
  if (row.protocolSigned) return 'row-signed';          // blue
  if (row.receiveMethod === 'self_pickup') return 'row-pickup'; // green
  return 'row-delivery';                               // yellow
}

function fmt(val) {
  return val && val !== 'NA' ? val : '—';
}

const SLOT_LABELS = {
  computer: 'PC / Computer', monitor1: 'Monitor 1', monitor2: 'Monitor 2', token: 'MFA Token', other: 'Other'
};

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB') + ' ' +
    new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function EquipmentLog() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterEmpId, setFilterEmpId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [signingId, setSigningId] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [historyModal, setHistoryModal] = useState({ open: false, name: '', rows: [], timeline: [], loading: false });
  const [editModal, setEditModal] = useState({ open: false, row: null });
  const [editForm, setEditForm] = useState({});
  const [editReason, setEditReason] = useState('');
  const [editSendEmail, setEditSendEmail] = useState('none');
  const [editEmail, setEditEmail] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const TAKE = 20;
  const [exporting, setExporting] = useState(false);
  const [photoModal, setPhotoModal] = useState({ open: false, loading: false, name: '', photos: [] });

  const openPhotoViewer = async (row) => {
    setPhotoModal({ open: true, loading: true, name: row.employeeName, photos: [] });
    try {
      const res = await fetch(`${API}/equipment-assignments/${row.id}/damage-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPhotoModal(prev => ({ ...prev, loading: false, photos: data.photos || [] }));
    } catch {
      setPhotoModal(prev => ({ ...prev, loading: false }));
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      // Fetch all records (no pagination)
      const params = new URLSearchParams({ skip: 0, take: 100000 });
      if (search) params.set('employeeId', search);
      const res = await fetch(`${API}/equipment-assignments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      const all = data.assignments || [];

      const cols = [
        { header: 'ID',              get: r => r.id },
        { header: 'Date Given',      get: r => r.createdAt ? new Date(r.createdAt).toLocaleString('en-GB') : '' },
        { header: 'Employee ID',     get: r => r.employeeId || '' },
        { header: 'Employee Name',   get: r => r.employeeName || '' },
        { header: 'Department',      get: r => r.departmentName || '' },
        { header: 'Country',         get: r => r.countryName || '' },
        { header: 'Method',          get: r => r.receiveMethod === 'self_pickup' ? 'Self-pickup' : 'Delivery' },
        { header: 'Address',         get: r => r.address || '' },
        { header: 'Type',            get: r => r.equipmentType || '' },
        { header: 'PC Serial',       get: r => r.computer || '' },
        { header: 'Monitor 1',       get: r => r.monitor1 || '' },
        { header: 'Monitor 2',       get: r => r.monitor2 || '' },
        { header: 'Token',           get: r => r.token || '' },
        { header: 'Other',           get: r => r.other || '' },
        { header: 'Given By',        get: r => r.createdBy ? `${r.createdBy.firstName} ${r.createdBy.lastName}` : '' },
        { header: 'Protocol Signed', get: r => r.protocolSigned ? 'Yes' : 'No' },
        { header: 'Signed At',       get: r => r.protocolSignedAt ? new Date(r.protocolSignedAt).toLocaleString('en-GB') : '' },
        { header: 'Sign IP',         get: r => r.signIpAddress || '' },
        { header: 'Returned',        get: r => r.returned ? 'Yes' : 'No' },
        { header: 'Returned At',     get: r => r.returnedAt ? new Date(r.returnedAt).toLocaleString('en-GB') : '' },
        { header: 'PC Returned',     get: r => r.computerReturned ? 'Yes' : 'No' },
        { header: 'Monitor1 Returned', get: r => r.monitor1Returned ? 'Yes' : 'No' },
        { header: 'Monitor2 Returned', get: r => r.monitor2Returned ? 'Yes' : 'No' },
        { header: 'Token Returned',  get: r => r.tokenReturned ? 'Yes' : 'No' },
        { header: 'Other Returned',  get: r => r.otherReturned ? 'Yes' : 'No' },
      ];

      const escape = val => {
        const s = String(val ?? '');
        // Wrap in quotes if it contains comma, quote, or newline
        return s.match(/[",\n]/) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const headerRow = cols.map(c => escape(c.header)).join(',');
      const dataRows  = all.map(r => cols.map(c => escape(c.get(r))).join(','));
      const csv = [headerRow, ...dataRows].join('\n');

      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      a.href     = url;
      a.download = `equipment_log_${date}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  const fetchLog = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const params = new URLSearchParams({ skip: page * TAKE, take: TAKE });
      if (search) params.set('employeeId', search);
      const res = await fetch(`${API}/equipment-assignments?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 401 || res.status === 403) { navigate('/admin'); return; }
      const data = await res.json();
      console.log('[EquipmentLog] API response:', res.status, data);
      if (!res.ok) {
        setFetchError(data.message || `Server error ${res.status}`);
        return;
      }
      setRows(data.assignments || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
      if (e.message === 'Failed to fetch') {
        setFetchError(`Cannot reach the server at ${API}. The backend may be starting up — please wait 30 seconds and refresh.`);
      } else {
        setFetchError(e.message || 'Failed to load equipment log.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, token, navigate]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  const handleSign = async (id) => {
    if (!window.confirm('Mark this protocol as signed by the employee?')) return;
    setSigningId(id);
    try {
      const res = await fetch(`${API}/equipment-assignments/${id}/sign`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchLog();
      } else {
        const data = await res.json().catch(() => ({}));
        alert('⚠ Failed to mark as signed: ' + (data.message || `Server error ${res.status}`));
      }
    } catch (e) {
      alert('⚠ Network error: ' + e.message);
    } finally {
      setSigningId(null);
    }
  };

  const handleResendSign = async (id, employeeName) => {
    if (!window.confirm(`Resend signature request email to ${employeeName}?`)) return;
    setResendingId(id);
    try {
      const res = await fetch(`${API}/equipment-assignments/${id}/resend-sign-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        alert('✓ Signature request email resent successfully.');
      } else {
        alert(data.message || 'Failed to resend email.');
      }
    } catch (e) {
      alert('Network error. Please try again.');
    } finally {
      setResendingId(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setSearch(filterEmpId);
  };

  const openHistory = async (id, name) => {
    setHistoryModal({ open: true, name, rows: [], timeline: [], loading: true });
    try {
      const res = await fetch(`${API}/equipment-assignments/${id}/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      // Merge lifecycle + audit events into a single timeline sorted by date desc
      const timeline = [
        ...(data.lifecycle || []),
        ...(data.auditEvents || [])
      ].sort((a, b) => new Date(b.date) - new Date(a.date));
      setHistoryModal({ open: true, name, rows: data.history || [], timeline, loading: false });
    } catch (e) {
      setHistoryModal({ open: true, name, rows: [], timeline: [], loading: false });
    }
  };

  const closeHistory = () => setHistoryModal({ open: false, name: '', rows: [], timeline: [], loading: false });

  const openEdit = (row) => {
    setEditForm({
      computer:      row.computer  || '',
      monitor1:      row.monitor1  || '',
      monitor2:      row.monitor2  || '',
      token:         row.token     || '',
      other:         row.other     || '',
      receiveMethod: row.receiveMethod || 'delivery',
      address:       row.address  || '',
    });
    setEditReason('');
    setEditSendEmail('none');
    setEditEmail(row.employeeEmail || '');
    setEditModal({ open: true, row });
  };

  const closeEdit = () => setEditModal({ open: false, row: null });

  const handleEditSave = async () => {
    if (!editReason.trim()) { alert('Please enter a reason for the change.'); return; }
    if (editSendEmail !== 'none' && !editEmail.trim()) { alert('Please enter an email address to send the document.'); return; }
    setSavingEdit(true);
    try {
      const res = await fetch(`${API}/equipment-assignments/${editModal.row.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...editForm, reason: editReason, sendEmail: editSendEmail, email: editEmail })
      });
      const data = await res.json();
      if (res.ok) {
        const emailMsg = data.emailResult?.sent ? ' Email sent.' : '';
        if (emailMsg) alert('Changes saved.' + emailMsg);
        closeEdit();
        fetchLog();
      } else {
        alert(data.message || 'Failed to update assignment.');
      }
    } catch (e) {
      alert('Network error. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="eq-log-page">
      <div className="eq-log-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>← Back</button>
        <h1>Equipment Assignment Log</h1>
        <div className="eq-legend">
          <span className="legend-chip green">Self-pickup</span>
          <span className="legend-chip yellow">Delivery</span>
          <span className="legend-chip blue">Protocol signed</span>
          <span className="legend-chip orange">Partial Return</span>
          <span className="legend-chip grey">Returned</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <form className="eq-search" onSubmit={handleSearch} style={{ margin: 20 }}>
          <input
            type="text"
            placeholder="Filter by Employee ID…"
            value={filterEmpId}
            onChange={e => setFilterEmpId(e.target.value)}
          />
          <button type="submit">Search</button>
          {search && <button type="button" onClick={() => { setSearch(''); setFilterEmpId(''); setPage(0); }}>Clear</button>}
        </form>
        <button
          onClick={exportCSV}
          disabled={exporting}
          style={{
            padding: '7px 16px',
            background: exporting ? '#94a3b8' : '#16a34a',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: '0.88rem',
            cursor: exporting ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap'
          }}
        >
          {exporting ? '⏳ Exporting…' : '⬇ Export CSV'}
        </button>
      </div>

      {fetchError && (
        <div style={{background:'#fff3cd',color:'#856404',border:'1px solid #ffc107',borderRadius:8,padding:'12px 16px',margin:'8px 0'}}>
          ⚠️ {fetchError}
          <button onClick={fetchLog} style={{marginLeft:12,padding:'4px 10px',cursor:'pointer'}}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="eq-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="eq-empty">No equipment assignments found.</div>
      ) : (
        <>
          <div className="eq-table-wrap">
            <table className="eq-table">
              <thead>
                <tr>
                  {[['#',''],['Date Given','createdAt'],['Employee ID','employeeId'],['Employee Name','employeeName'],
                    ['Department','departmentName'],['Method','receiveMethod'],['Address',''],
                    ['Type','equipmentType'],['PC Serial','computer'],['Monitor 1','monitor1'],
                    ['Monitor 2','monitor2'],['Token','token'],['Other','other'],
                    ['Given By',''],['Protocol','protocolSigned'],['Returned','returned'],['Action','']].map(([label, key]) => (
                    <th key={label}
                      onClick={key ? () => { if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortBy(key); setSortDir('asc'); } } : undefined}
                      style={key ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } : { whiteSpace: 'nowrap' }}
                      title={key ? `Sort by ${label}` : ''}
                    >
                      {label}{key && sortBy === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : (key ? ' ▽' : '')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...rows].sort((a, b) => {
                  if (!sortBy) return 0;
                  const va = a[sortBy] ?? '';
                  const vb = b[sortBy] ?? '';
                  const cmp = typeof va === 'boolean'
                    ? (va === vb ? 0 : va ? -1 : 1)
                    : String(va).localeCompare(String(vb), undefined, { numeric: true });
                  return sortDir === 'asc' ? cmp : -cmp;
                }).map((r, i) => (
                  <tr key={r.id} className={getRowClass(r)}>
                    <td>{page * TAKE + i + 1}</td>
                    <td className="nowrap">{fmtDate(r.createdAt)}</td>
                    <td>{r.employeeId}</td>
                    <td>{r.employeeName}</td>
                    <td>{fmt(r.departmentName || r.department?.name)}</td>
                    <td>
                      <span className={`method-badge ${r.receiveMethod === 'self_pickup' ? 'badge-green' : 'badge-yellow'}`}>
                        {r.receiveMethod === 'self_pickup' ? 'Self-pickup' : 'Delivery'}
                      </span>
                    </td>
                    <td className="addr-cell">{r.receiveMethod === 'self_pickup' ? '—' : fmt(r.address)}</td>
                    <td>{r.equipmentType === 'new'
                      ? <span className="method-badge badge-type-new">New</span>
                      : <span className="method-badge badge-type-change">Change</span>}</td>
                    <td>{fmt(r.computer)}</td>
                    <td>{fmt(r.monitor1)}</td>
                    <td>{fmt(r.monitor2)}</td>
                    <td>{fmt(r.token)}</td>
                    <td>{fmt(r.other)}</td>
                    <td className="nowrap">
                      {r.createdBy ? `${r.createdBy.firstName} ${r.createdBy.lastName}` : '—'}
                    </td>
                    <td>
                      {r.protocolSigned ? (
                        <span className="signed-badge">
                          ✔ Signed<br />
                          <small>{fmtDate(r.protocolSignedAt)}</small>
                        </span>
                      ) : (
                        <span className="unsigned-badge">Pending</span>
                      )}
                    </td>
                    <td>
                      {r.returned ? (
                        <span className="returned-badge">
                          ✔ Returned<br />
                          <small>{fmtDate(r.returnedAt)}</small>
                        </span>
                      ) : (['computer','monitor1','monitor2','token','other'].some(k => r[k + 'Returned'])) ? (
                        <span className="partial-badge">
                          ⚠ Partial<br />
                          <small>{fmtDate(r.returnedAt || r.updatedAt)}</small>
                        </span>
                      ) : (
                        <span className="unsigned-badge">Active</span>
                      )}
                    </td>
                    <td>
                      {!r.protocolSigned && (
                        <button
                          className="btn-sign"
                          disabled={signingId === r.id}
                          onClick={() => handleSign(r.id)}
                        >
                          {signingId === r.id ? '…' : 'Mark Signed'}
                        </button>
                      )}
                      {!r.protocolSigned && r.employeeEmail && (
                        <button
                          className="btn-resend-sign"
                          disabled={resendingId === r.id}
                          onClick={() => handleResendSign(r.id, r.employeeName)}
                          title="Resend signature request email to employee"
                        >
                          {resendingId === r.id ? '…' : '✉ Resend Link'}
                        </button>
                      )}
                      <button
                        className="btn-history"
                        onClick={() => openHistory(r.id, r.employeeName)}
                        title="View item change history"
                      >
                        History
                      </button>
                      <button
                        className="btn-sign"
                        style={{ background: '#f59e0b', marginTop: 4 }}
                        onClick={() => openEdit(r)}
                        title="Edit equipment serials or delivery details"
                      >
                        ✏ Edit
                      </button>
                      {r.damagePhotoUrls?.length > 0 && (
                        <button
                          className="btn-sign"
                          style={{ background: '#7c3aed', marginTop: 4 }}
                          onClick={() => openPhotoViewer(r)}
                          title="View damage photos"
                        >
                          📷 {r.damagePhotoUrls.length} Photo{r.damagePhotoUrls.length !== 1 ? 's' : ''}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="eq-pagination">
            <span>{total} total record{total !== 1 ? 's' : ''}</span>
            <div className="pg-btns">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
              <span>Page {page + 1} / {Math.max(1, Math.ceil(total / TAKE))}</span>
              <button disabled={(page + 1) * TAKE >= total} onClick={() => setPage(p => p + 1)}>Next ›</button>
            </div>
          </div>
        </>
      )}

      {/* Edit Modal */}
      {editModal.open && editModal.row && (
        <div className="history-overlay" onClick={closeEdit}>
          <div className="history-modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
            <div className="history-modal-header">
              <h3>✏ Edit Assignment</h3>
              <span className="history-emp-name">{editModal.row.employeeName} ({editModal.row.employeeId})</span>
              <button className="history-close" onClick={closeEdit}>&times;</button>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[['computer','PC / Computer'],['monitor1','Monitor 1'],['monitor2','Monitor 2'],['token','MFA Token / OneSpan'],['other','Other']].map(([key, label]) => (
                <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>{label}</label>
                  <input
                    type="text"
                    value={editForm[key]}
                    onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={`Enter ${label} serial / model…`}
                    style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                  />
                </div>
              ))}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Receive Method</label>
                <select
                  value={editForm.receiveMethod}
                  onChange={e => setEditForm(f => ({ ...f, receiveMethod: e.target.value }))}
                  style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                >
                  <option value="delivery">Delivery</option>
                  <option value="self_pickup">Self-pickup</option>
                </select>
              </div>
              {editForm.receiveMethod === 'delivery' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Delivery Address</label>
                  <input
                    type="text"
                    value={editForm.address}
                    onChange={e => setEditForm(f => ({ ...f, address: e.target.value }))}
                    placeholder="Enter delivery address…"
                    style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#e53e3e' }}>Reason for Change *</label>
                <input
                  type="text"
                  value={editReason}
                  onChange={e => setEditReason(e.target.value)}
                  placeholder="e.g. Wrong serial entered, replacement unit…"
                  style={{ padding: '7px 10px', border: '1px solid #e53e3e', borderRadius: 6, fontSize: 13 }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>After saving, send email to employee?</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[['none', '🚫 No email'], ['protocol', '📧 Send updated PDF'], ['sign', '✏️ Reset & request new signature']].map(([v, lbl]) => (
                    <button key={v} type="button" onClick={() => setEditSendEmail(v)}
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid', fontSize: 12, cursor: 'pointer', fontWeight: 600,
                        background: editSendEmail === v ? '#667eea' : '#f0f0f0',
                        color: editSendEmail === v ? '#fff' : '#555',
                        borderColor: editSendEmail === v ? '#667eea' : '#ccc' }}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {editModal.row && editModal.row.protocolSigned && editSendEmail === 'sign' && (
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: '#e53e3e' }}>
                    ⚠️ This will <strong>reset the signature</strong> and send a new signing link. The employee must re-sign.
                  </p>
                )}
              </div>
              {editSendEmail !== 'none' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Employee Email</label>
                  <input
                    type="email"
                    value={editEmail}
                    onChange={e => setEditEmail(e.target.value)}
                    placeholder="employee@company.com"
                    style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={closeEdit} style={{ padding: '8px 18px', background: '#e2e8f0', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button
                  onClick={handleEditSave}
                  disabled={savingEdit}
                  style={{ padding: '8px 18px', background: '#667eea', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
                >
                  {savingEdit ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModal.open && (
        <div className="history-overlay" onClick={closeHistory}>
          <div className="history-modal" onClick={e => e.stopPropagation()}>
            <div className="history-modal-header">
              <h3>Equipment Operation History</h3>
              <span className="history-emp-name">{historyModal.name}</span>
              <button className="history-close" onClick={closeHistory}>&times;</button>
            </div>
            {historyModal.loading ? (
              <div className="history-loading">Loading&hellip;</div>
            ) : (historyModal.rows.length === 0 && historyModal.timeline.length === 0) ? (
              <div className="history-empty">No history recorded for this assignment.<br/><small>Events are tracked automatically when the assignment is created, signed, returned, or items are updated.</small></div>
            ) : (
              <div>
                {/* Lifecycle & Audit timeline */}
                {historyModal.timeline.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, paddingLeft: 4 }}>Operations Timeline</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {historyModal.timeline.map((ev, i) => {
                        const colors = {
                          CREATED: '#22c55e', PROTOCOL_SIGNED: '#3b82f6', DELIVERY_CONFIRMED: '#8b5cf6',
                          RETURNED: '#f59e0b', RETURN_SIGNED: '#ef4444',
                          EQUIPMENT_ASSIGNED: '#22c55e', EQUIPMENT_RETURNED: '#f59e0b',
                          RETURN_PROTOCOL_SIGNED: '#ef4444'
                        };
                        const color = colors[ev.action] || '#6b7280';
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 8px', background: '#f9fafb', borderRadius: 8, borderLeft: `3px solid ${color}` }}>
                            <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap', minWidth: 110 }}>{fmtDate(ev.date)}</span>
                            <span style={{ background: color, color: '#fff', padding: '1px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{ev.action.replace(/_/g, ' ')}</span>
                            <span style={{ fontSize: 12, color: '#374151', flex: 1 }}>{ev.detail || ''}</span>
                            {ev.by && <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>by {ev.by}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Item change history table */}
                {historyModal.rows.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8, paddingLeft: 4 }}>Item Changes</h4>
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Slot</th>
                    <th>Previous</th>
                    <th>New</th>
                    <th>Reason</th>
                    <th>Changed By</th>
                  </tr>
                </thead>
                <tbody>
                  {historyModal.rows.map(h => (
                    <tr key={h.id}>
                      <td className="nowrap">{fmtDate(h.changedAt)}</td>
                  <td><span className="hist-slot">{SLOT_LABELS[h.slot] || h.slot}</span></td>
                      <td className="hist-prev">{h.prevValue || <em>empty</em>}</td>
                      <td className="hist-new">{h.newValue || <em>empty</em>}</td>
                      <td>{h.reason || '—'}</td>
                      <td className="nowrap">{h.changedBy ? `${h.changedBy.firstName} ${h.changedBy.lastName}` : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Damage Photo Viewer Modal */}
      {photoModal.open && (
        <div className="history-overlay" onClick={() => setPhotoModal({ open: false, loading: false, name: '', photos: [] })}>
          <div className="history-modal" style={{ maxWidth: 760 }} onClick={e => e.stopPropagation()}>
            <div className="history-modal-header">
              <h3>📷 Damage Photos</h3>
              <span className="history-emp-name">{photoModal.name}</span>
              <button className="history-close" onClick={() => setPhotoModal({ open: false, loading: false, name: '', photos: [] })}>&times;</button>
            </div>
            {photoModal.loading ? (
              <div className="history-loading">Loading photos…</div>
            ) : photoModal.photos.length === 0 ? (
              <div className="history-empty">No damage photos found.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, padding: '12px 0' }}>
                {photoModal.photos.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer">
                    <img
                      src={url}
                      alt={`Damage evidence ${i + 1}`}
                      style={{ width: 200, height: 150, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer' }}
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
